import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../../../../api/axiosInstance";
// import Footer from "../../../../components/footer/Footer";
import { ShieldCheck, ChevronRight, Star, Award, Home } from "lucide-react";

const ServiceDescription = ({ description }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <div className="space-y-1">
      <p className={`text-gray-600 text-sm md:text-base leading-relaxed font-medium ${!isExpanded ? 'line-clamp-2' : ''}`}>
        {description}
      </p>
      {description.length > 100 && (
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-[#134DA7] text-xs font-bold uppercase tracking-wider hover:text-[#0097b2] transition-colors"
        >
          {isExpanded ? "View Less" : "View More"}
        </button>
      )}
    </div>
  );
};

const VendorDetails = () => {
  const { vendorId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedServiceId = location.state?.serviceId || null;

  const [vendor, setVendor] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("about");
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const fetchVendorDetails = async () => {
      try {
        const response = await axiosInstance.get(`/users/vendors/${vendorId}`);
        const vendorData = response.data.vendor;
        setVendor(vendorData);

        const allServices = vendorData.services || [];
        if (selectedServiceId) {
          const filtered = allServices.filter(
            (s) => String(s.serviceId) === String(selectedServiceId)
          );
          setServices(filtered.length > 0 ? filtered : allServices);
        } else {
          setServices(allServices);
        }
      } catch (error) {
        console.error("❌ Error fetching vendor details:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchReviews = async () => {
      try {
        const response = await axiosInstance.get(`/users/reviews/${vendorId}`);
        setReviews(response.data.reviews || []);
      } catch (error) {
        console.error("❌ Error fetching reviews:", error);
      }
    };

    fetchVendorDetails();
    fetchReviews();
  }, [vendorId, selectedServiceId]);

  const handleBookNow = (service, type = "audioCall") => {
    const finalPrice = service.prices?.[type] || service.prices?.audioCall || 0;
    navigate(`/book-appointment/${encodeURIComponent(service.title)}`, {
      state: { 
        vendorId, 
        serviceTitle: service.title, 
        serviceId: service.serviceId, 
        price: finalPrice,
        bookingType: type 
      },
    });
  };

  if (loading) return (
    <div className="flex justify-center items-center h-screen bg-white">
      <div className="w-10 h-10 border-4 border-[#134DA7] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!vendor) return <div className="text-center p-10 font-bold text-slate-700">Vendor not found.</div>;

  const initials = vendor.name
    ? vendor.name
        .split(" ")
        .filter(Boolean)
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "EX";

  const hasNoImage =
    !vendor.profileImage ||
    vendor.profileImage.includes("flaticon.com") ||
    vendor.profileImage.includes("149071.png") ||
    imgError;

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col font-outfit">
      
      {/* Solid Dark Hero Section */}
      <section className="relative pt-24 pb-24 px-6 bg-[#020617]">
        <div className="container mx-auto relative z-10 max-w-6xl text-center">
          {/* Breadcrumbs */}
          <nav className="flex items-center justify-center gap-2 text-sm font-medium text-slate-300 mb-8">
            <button onClick={() => navigate('/home')} className="hover:text-white transition-colors flex items-center gap-1">
              <Home size={14} /> Home
            </button>
            <ChevronRight size={14} className="opacity-50" />
            <button onClick={() => navigate('/services')} className="hover:text-white transition-colors">
              Services
            </button>
            <ChevronRight size={14} className="opacity-50" />
            <button onClick={() => navigate(-1)} className="hover:text-white transition-colors">
              Experts
            </button>
            <ChevronRight size={14} className="opacity-50" />
            <span className="text-white capitalize">{vendor.name}</span>
          </nav>
        </div>
      </section>

      <div className="container mx-auto max-w-4xl px-6 pb-32 relative z-20 flex-grow -mt-16">
        
        {/* Vendor Profile Card */}
        <div className="bg-white rounded-[2rem] p-6 md:p-8 shadow-sm border border-gray-100 mb-8">
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div className="relative">
              <div className="w-28 h-28 rounded-2xl overflow-hidden border border-slate-100 shadow-sm flex items-center justify-center bg-slate-50 shrink-0">
                {hasNoImage ? (
                  <div className="w-full h-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white font-extrabold text-3xl tracking-wider">
                    {initials}
                  </div>
                ) : (
                  <img 
                    src={vendor.profileImage} 
                    alt={vendor.name} 
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                )}
              </div>
              {(vendor.documentsOverallStatus === "Verified" || vendor.isVerified) && (
                <div className="absolute -bottom-2 -right-2 bg-blue-600 p-1.5 rounded-lg text-white shadow-md">
                  <ShieldCheck size={18} />
                </div>
              )}
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight mb-1 capitalize">
                {vendor.name}
              </h2>
              <p className="text-[#134DA7] font-semibold text-sm mb-4">
                {vendor.specialization || "Technical Consultant"}
              </p>
              
              <div className="flex flex-wrap justify-center md:justify-start gap-3">
                <div className="flex items-center gap-1.5 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-100">
                  <Star size={14} className="text-amber-500 fill-amber-500" />
                  <span className="text-xs font-bold">{vendor.averageRating || "New"}</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-100">
                  <Award size={14} className="text-[#134DA7]" />
                  <span className="text-xs font-bold">{vendor.experienceYears || 0} Years Exp</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 mb-8">
          {[
            { id: "about", label: "About" },
            { id: "services", label: `Services Offered (${services.length})` },
            { id: "reviews", label: `Reviews (${reviews.length})` }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-center text-sm font-bold transition-all relative ${
                activeTab === tab.id
                  ? "text-blue-600"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="animate-fadeIn">
          {activeTab === "about" && (
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Professional Bio</h4>
                <p className="text-slate-600 text-sm md:text-base leading-relaxed font-medium whitespace-pre-line">
                  {vendor.description || "No bio description available yet."}
                </p>
              </div>

              <div className="border-t border-slate-100 pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {vendor.email && (
                  <div>
                    <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Email Address</h5>
                    <p className="text-slate-700 text-sm font-semibold mt-0.5">{vendor.email}</p>
                  </div>
                )}
                {vendor.city && (
                  <div>
                    <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Office Location</h5>
                    <p className="text-slate-700 text-sm font-semibold mt-0.5">{vendor.city}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "services" && (
            <div className="grid grid-cols-1 gap-6">
              {services.length === 0 ? (
                <div className="bg-white rounded-3xl py-12 text-center border border-slate-100">
                  <p className="text-slate-400 text-sm font-medium">No services offered yet.</p>
                </div>
              ) : (
                services.map((service, idx) => (
                  <div 
                    key={idx} 
                    className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm flex flex-col lg:flex-row items-start justify-between gap-8"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <h4 className="text-lg font-bold text-slate-800">{service.title}</h4>
                        {service.experience && (
                          <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-md">
                            {service.experience}Y Exp
                          </span>
                        )}
                      </div>
                      <ServiceDescription description={service.description} />
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto mt-4 lg:mt-0">
                      <div className="flex gap-2 w-full sm:w-auto justify-center">
                        {service.prices?.audioCall > 0 && (
                          <div className="bg-slate-50 px-4 py-2 rounded-xl text-center border border-slate-200/50 min-w-[80px]">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Audio</p>
                            <p className="text-base font-bold text-blue-600">₹{service.prices.audioCall}</p>
                          </div>
                        )}
                        {service.prices?.videoCall > 0 && (
                          <div className="bg-slate-50 px-4 py-2 rounded-xl text-center border border-slate-200/50 min-w-[80px]">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Video</p>
                            <p className="text-base font-bold text-blue-600">₹{service.prices.videoCall}</p>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => handleBookNow(service, "videoCall")}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-6 rounded-xl font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 w-full sm:w-auto active:scale-[0.98] shadow-md shadow-blue-500/10"
                      >
                        Book Now <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === "reviews" && (
            <div className="space-y-4">
              {reviews.length === 0 ? (
                <div className="bg-white rounded-3xl py-12 text-center border border-slate-100">
                  <p className="text-slate-400 text-sm font-medium">No reviews yet.</p>
                </div>
              ) : (
                reviews.map((review, index) => (
                  <div key={index} className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center gap-1 bg-amber-400 text-white px-2 py-0.5 rounded-lg text-xs font-bold">
                        <Star size={12} className="fill-current" />
                        <span>{review.rating}</span>
                      </div>
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Verified Client</span>
                    </div>
                    <p className="text-slate-600 text-sm font-medium italic">"{review.comment || "Great service!"}"</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorDetails;